import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Heart, 
  Calendar, 
  Mail, 
  MapPin, 
  ChevronDown, 
  ExternalLink,
  Facebook,
  Menu,
  X,
  Check,
  Loader2,
  ArrowRight,
  Users,
  Shield,
  HandHeart,
  Eye,
  Handshake,
  Lock,
  Quote,
  Plus,
  Star,
  ImageIcon,
  Instagram,
  Target,
  Gift
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_page-demo-1/artifacts/odgtd01j_Gemini_Generated_Image_6m8u876m8u876m8u.png";

// Organization Photos
const PHOTOS = {
  founder_brochures: "https://customer-assets.emergentagent.com/job_page-demo-1/artifacts/6mr03uy8_475972418_470720839447089_8202792857595117448_n.jpg",
  founder_memorial: "https://customer-assets.emergentagent.com/job_page-demo-1/artifacts/y18mrt1m_475653309_470006699518503_453545757114088053_n.jpg",
  community_group: "https://customer-assets.emergentagent.com/job_page-demo-1/artifacts/e6x5wv4w_476804241_474948855690954_3172028515560017449_n.jpg",
  picnic_grill: "https://customer-assets.emergentagent.com/job_page-demo-1/artifacts/8rnmiqt1_470156204_435821729603667_2756188160096230797_n.jpg",
  picnic_friends: "https://customer-assets.emergentagent.com/job_page-demo-1/artifacts/ty6bbk1w_470218785_435821906270316_8335598670076670319_n.jpg",
  community_smiles: "https://customer-assets.emergentagent.com/job_page-demo-1/artifacts/2kgu84y1_Untitled%20design%20-%202026-03-24T143321.112.png"
};

// Butterfly SVG Component
const Butterfly = ({ className = "", color = "#ea580c" }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M50 25C45 15 35 5 20 5C5 5 0 20 5 35C10 50 25 55 50 50C75 55 90 50 95 35C100 20 95 5 80 5C65 5 55 15 50 25Z" fill={color} opacity="0.8"/>
    <path d="M50 75C45 85 35 95 20 95C5 95 0 80 5 65C10 50 25 45 50 50C75 45 90 50 95 65C100 80 95 95 80 95C65 95 55 85 50 75Z" fill={color} opacity="0.6"/>
    <ellipse cx="50" cy="50" rx="3" ry="30" fill="#1e3a8a"/>
  </svg>
);

// Navigation Component
const Navigation = ({ activeSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { id: "home", label: "Home" },
    { id: "mission", label: "Our Mission" },
    { id: "founder", label: "Our Story" },
    { id: "memorial", label: "Memorial Wall" },
    { id: "events", label: "Events" },
    { id: "donate", label: "Donate" },
    { id: "contact", label: "Contact" }
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <motion.nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "nav-glass border-b border-orange-100/50 shadow-sm" : "bg-transparent"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div 
            className="flex items-center gap-3 cursor-pointer"
            whileHover={{ scale: 1.02 }}
            onClick={() => scrollToSection("home")}
            data-testid="logo-link"
          >
            <img 
              src={LOGO_URL} 
              alt="Chaces Way Logo" 
              className="h-14 md:h-16 w-auto object-contain"
            />
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.slice(0, -1).map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => scrollToSection(item.id)}
                className={`font-medium transition-colors hover:text-[#ea580c] ${
                  activeSection === item.id ? "text-[#ea580c]" : "text-slate-600"
                }`}
              >
                {item.label}
              </button>
            ))}
            <motion.button
              data-testid="nav-donate-btn"
              onClick={() => scrollToSection("donate")}
              className="bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Heart className="w-4 h-4 inline mr-2" />
              Donate
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2 text-[#1e3a8a]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden nav-glass border-t border-orange-100/50"
          >
            <div className="px-6 py-4 space-y-3">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  onClick={() => scrollToSection(item.id)}
                  className="block w-full text-left py-2 font-medium text-slate-600 hover:text-[#ea580c] transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section with Welcome Message
const HeroSection = () => {
  return (
    <section 
      id="home" 
      data-testid="hero-section home-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #eff6ff 0%, #fff7ed 50%, #fefce8 100%)"
      }}
    >
      {/* Decorative Butterflies */}
      <motion.div 
        className="absolute top-32 left-[10%] opacity-30"
        animate={{ y: [-10, 10, -10], rotate: [-5, 5, -5] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        <Butterfly className="w-24 h-24" color="#ea580c" />
      </motion.div>
      <motion.div 
        className="absolute top-48 right-[15%] opacity-20"
        animate={{ y: [10, -15, 10], rotate: [3, -3, 3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      >
        <Butterfly className="w-16 h-16" color="#3b82f6" />
      </motion.div>
      <motion.div 
        className="absolute bottom-32 left-[20%] opacity-25"
        animate={{ y: [-5, 15, -5], rotate: [-3, 5, -3] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      >
        <Butterfly className="w-20 h-20" color="#1e3a8a" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="inline-block font-sans text-sm font-semibold text-[#ea580c] tracking-widest uppercase mb-4">
              Let's Meet At The Corner Of Hope and Healing
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e3a8a] leading-tight mb-6">
              Welcome to 
              <span className="gradient-text block mt-2">Chace's Way</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-6">
              Welcome to our support group for bereaved parents and siblings. We understand the pain 
              and difficult emotions that come with losing a child or sibling, and we're here to offer 
              support and comfort to help each other through this difficult time.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              Our group is a safe, welcoming space where you can share your feelings, experiences, 
              or just listen to others who may be going through similar experiences. We believe that 
              by coming together, we can help each other heal and find hope for tomorrow.
            </p>
            
            {/* Founder Quote */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-100/50 shadow-sm mb-8">
              <Quote className="w-8 h-8 text-[#ea580c]/30 mb-2" />
              <p className="text-slate-700 italic mb-3">
                "By coming together, we can help each other heal and find hope for tomorrow."
              </p>
              <p className="text-[#1e3a8a] font-semibold">— Andrea Jones, Executive Director</p>
            </div>

            <div className="flex flex-wrap gap-4">
              <motion.a
                href="#donate"
                data-testid="hero-donate-btn"
                className="inline-flex items-center gap-2 bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("donate")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <Heart className="w-5 h-5" />
                Support Our Mission
              </motion.a>
              <motion.a
                href="#mission"
                data-testid="hero-learn-more-btn"
                className="inline-flex items-center gap-2 bg-white text-[#1e3a8a] border-2 border-[#1e3a8a]/20 hover:border-[#1e3a8a] font-semibold px-8 py-4 rounded-full transition-all"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("mission")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Learn More
                <ChevronDown className="w-5 h-5" />
              </motion.a>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1641057324798-c05769de628a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Black hands joined together in support and unity"
                className="w-full h-[400px] md:h-[500px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/30 to-transparent" />
            </div>
            {/* Floating Card */}
            <motion.div
              className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl max-w-[220px]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#ea580c]/10 rounded-full flex items-center justify-center">
                  <Heart className="w-5 h-5 text-[#ea580c]" />
                </div>
                <span className="font-serif font-bold text-[#1e3a8a]">Community</span>
              </div>
              <p className="text-sm text-slate-500">Together, we heal. Together, we remember.</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Wave Divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L60 105C120 90 240 60 360 55C480 50 600 70 720 75C840 80 960 70 1080 65C1200 60 1320 60 1380 60L1440 60V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#fffaf0"/>
        </svg>
      </div>
    </section>
  );
};

// Mission, Vision, Values Section
const MissionSection = () => {
  const values = [
    { icon: HandHeart, title: "Compassion", desc: "We show empathy for all those experiencing the pain of loss." },
    { icon: Users, title: "Inclusivity", desc: "Everyone is welcome, regardless of age, race, gender, sexual orientation, or religion." },
    { icon: Lock, title: "Confidentiality", desc: "We respect the privacy of our members and hold their trust in the highest regard." },
    { icon: Shield, title: "Respect", desc: "We listen to each other's experiences without judgement and honor each person's unique journey." },
    { icon: Handshake, title: "Collaboration", desc: "We create a supportive community as we work together for healing and growth." }
  ];

  return (
    <section id="mission" data-testid="mission-section" className="py-24 md:py-32 bg-[#fffaf0] relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="inline-block font-sans text-sm font-semibold text-[#ea580c] tracking-widest uppercase mb-4">
            Our Purpose
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1e3a8a] mb-8">
            Mission Statement
          </h2>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-4xl mx-auto">
            Our mission is to provide support, comfort, and healing to bereaved parents and siblings 
            who have lost a child or sibling. We aim to create a safe, non-judgmental space where 
            individuals can share their stories, experiences, and emotions with others who understand 
            what they are going through. Our goal is to help individuals in our group navigate the 
            difficult journey of grief, find hope, and move forward at their own pace.
          </p>
        </motion.div>

        {/* Vision & Target Audience Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {/* Vision */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-3xl p-8 text-white relative overflow-hidden"
          >
            <Butterfly className="absolute -right-8 -bottom-8 w-32 h-32 opacity-10" color="#ffffff" />
            <div className="relative z-10">
              <Eye className="w-12 h-12 mb-4 text-[#ea580c]" />
              <h3 className="font-serif text-2xl font-bold mb-4">Our Vision</h3>
              <p className="text-blue-100 leading-relaxed">
                Our vision is to create a world where individuals and families facing the loss 
                of a child or sibling have access to a safe and supportive community that helps 
                them understand their feelings, recognize their strengths, and move forward with hope.
              </p>
            </div>
          </motion.div>

          {/* Target Audience */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl p-8 shadow-lg border border-orange-100/50"
          >
            <Users className="w-12 h-12 mb-4 text-[#1e3a8a]" />
            <h3 className="font-serif text-2xl font-bold text-[#1e3a8a] mb-4">Who We Serve</h3>
            <p className="text-slate-600 leading-relaxed">
              Our programs and services are designed for parents and siblings who have lost a 
              child or sibling at any age, including miscarriage, stillbirth, or the loss of 
              an adult child. We aim to provide a safe, inclusive space where individuals can 
              connect with others who share similar experiences, concerns, and feelings.
            </p>
          </motion.div>
        </div>

        {/* Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#1e3a8a] mb-4">Our Values</h3>
          <p className="text-slate-600 max-w-2xl mx-auto">Our organization is guided by these core principles</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100/50 hover:shadow-md transition-all text-center"
            >
              <div className="w-14 h-14 mx-auto bg-gradient-to-br from-[#1e3a8a]/10 to-[#ea580c]/10 rounded-2xl flex items-center justify-center mb-4">
                <value.icon className="w-7 h-7 text-[#1e3a8a]" />
              </div>
              <h4 className="font-serif font-bold text-[#1e3a8a] mb-2">{value.title}</h4>
              <p className="text-slate-600 text-sm leading-relaxed">{value.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Community Photo Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-20 mb-12"
        >
          <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#1e3a8a] mb-4">Our Community</h3>
          <p className="text-slate-600 max-w-2xl mx-auto">Moments of connection, healing, and hope</p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { src: PHOTOS.community_group, alt: "Community gathering at memorial picnic" },
            { src: PHOTOS.picnic_grill, alt: "Community cookout event" },
            { src: PHOTOS.community_smiles, alt: "Community members together" }
          ].map((photo, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="relative group overflow-hidden rounded-2xl aspect-square"
            >
              <img
                src={photo.src}
                alt={photo.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <p className="text-white text-sm font-medium">{photo.alt}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Founder Story Section
const FounderSection = () => {
  return (
    <section id="founder" data-testid="founder-section" className="py-24 md:py-32 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("https://images.unsplash.com/photo-1710068145623-e972cc61ac10?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200")`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block font-sans text-sm font-semibold text-[#ea580c] tracking-widest uppercase mb-4">
            Our Beginning
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1e3a8a] mb-6">
            The Founder's Story
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-5 gap-12 items-start">
          {/* Story Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="md:col-span-2"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#1e3a8a]/10 to-[#ea580c]/10 rounded-[2.5rem] -rotate-3" />
              <img
                src={PHOTOS.founder_brochures}
                alt="Andrea Jones, Founder of Chaces Way"
                className="relative rounded-[2rem] shadow-xl w-full h-[400px] object-cover object-top"
                data-testid="founder-image"
              />
            </div>
            {/* Quote Card */}
            <motion.div
              className="mt-8 bg-[#1e3a8a] text-white rounded-2xl p-6 shadow-xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Quote className="w-8 h-8 text-[#ea580c] mb-3" />
              <p className="text-blue-100 italic leading-relaxed">
                "Grief requires a community that is not only accepting but also challenges 
                people to come together in solidarity."
              </p>
              <p className="mt-3 font-semibold">— Andrea Jones, Executive Director</p>
            </motion.div>
          </motion.div>

          {/* Story Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="md:col-span-3 space-y-6"
          >
            <p className="text-lg text-slate-600 leading-relaxed">
              <span className="font-serif font-bold text-[#1e3a8a] text-xl">Andrea Jones</span> is a mother 
              who has experienced the unimaginable pain of losing her child to SIDS (Sudden Infant Death Syndrome). 
              Her son passed away at just 7 weeks old, but her love for him never faded away.
            </p>
            
            <p className="text-slate-600 leading-relaxed">
              Although she discovered her son too late, she acted with incredible bravery and administered CPR, 
              affecting the course of her life and his care dramatically. Her son was placed on life support for 
              the next seven years. Despite the hardship, Andrea never gave up on her son and faced the challenges 
              of caring for him head-on.
            </p>

            <p className="text-slate-600 leading-relaxed">
              Through her son's journey and her own experiences, Andrea has gained firsthand knowledge of the 
              devastating effects of losing a child and having to live through the pain and challenges of 
              long-term hospitalization. She has found a new purpose in starting a non-profit organization 
              dedicated to supporting bereaved parents and siblings.
            </p>

            <p className="text-slate-600 leading-relaxed">
              Andrea's non-profit provides accessible resources, counseling, and group support, recognizing 
              the importance of a supportive community in the grieving journey. Her vision is to create a 
              network of support that ensures that <strong>no parent or sibling will have to journey alone 
              through loss</strong>.
            </p>

            <div className="bg-gradient-to-r from-[#fef3e2] to-[#fff7ed] rounded-2xl p-6 mt-8">
              <h4 className="font-serif font-bold text-[#1e3a8a] mb-3">Andrea's Vision</h4>
              <p className="text-slate-600 leading-relaxed">
                She envisions Chace's Way as a place of comfort, where group support, education, and 
                specialized services are brought together to meet the unique needs of bereaved parents 
                and siblings. Andrea's drive and passion for the bereavement community have helped guide 
                her on this path of creating a resource that can provide hope, healing, and empowerment 
                to those navigating through the loss of a loved one.
              </p>
            </div>

            <p className="text-slate-700 font-medium italic">
              She is an inspiration, having lived through such immense pain, and still finding the 
              strength to support others in their moment of need.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Memorial Wall Section
const MemorialWallSection = () => {
  const [memorials, setMemorials] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formData, setFormData] = useState({
    child_name: "",
    photo_url: "",
    memory: "",
    submitted_by: "",
    relationship: "",
    birth_date: "",
    angel_date: ""
  });

  // Fetch memorials on mount
  useEffect(() => {
    fetchMemorials();
  }, []);

  const fetchMemorials = async () => {
    try {
      const response = await axios.get(`${API}/memorials`);
      setMemorials(response.data);
    } catch (error) {
      console.error("Error fetching memorials:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/memorials`, formData);
      setSubmitStatus({ type: "success", message: "Thank you for sharing this beautiful memory. Your tribute has been added to our Memorial Wall." });
      setFormData({
        child_name: "",
        photo_url: "",
        memory: "",
        submitted_by: "",
        relationship: "",
        birth_date: "",
        angel_date: ""
      });
      setShowForm(false);
      fetchMemorials();
    } catch (error) {
      setSubmitStatus({ type: "error", message: "Failed to submit memorial. Please try again." });
    }
    setIsSubmitting(false);
  };

  // Default memorial butterfly image
  const defaultImage = "https://images.unsplash.com/photo-1712540615870-f9664a06ea5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=400";

  return (
    <section id="memorial" data-testid="memorial-section" className="py-24 md:py-32 bg-gradient-to-b from-white to-[#fffaf0] relative overflow-hidden">
      {/* Decorative butterflies */}
      <motion.div 
        className="absolute top-20 right-[10%] opacity-20"
        animate={{ y: [-5, 10, -5], rotate: [-3, 5, -3] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        <Butterfly className="w-20 h-20" color="#ea580c" />
      </motion.div>
      <motion.div 
        className="absolute bottom-32 left-[5%] opacity-15"
        animate={{ y: [5, -10, 5], rotate: [3, -3, 3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      >
        <Butterfly className="w-16 h-16" color="#1e3a8a" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block font-sans text-sm font-semibold text-[#ea580c] tracking-widest uppercase mb-4">
            Forever In Our Hearts
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1e3a8a] mb-6">
            Memorial Wall
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed mb-8">
            A sacred space to honor and remember our beloved angel children. Share their stories, 
            their light, and keep their memories alive in our hearts forever.
          </p>
          
          <motion.button
            data-testid="add-memorial-btn"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 bg-[#1e3a8a] hover:bg-[#1e40af] text-white font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus className="w-5 h-5" />
            Add a Memorial
          </motion.button>
        </motion.div>

        {/* Success/Error Message */}
        <AnimatePresence>
          {submitStatus && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`mb-8 p-6 rounded-2xl max-w-2xl mx-auto ${
                submitStatus.type === "success" ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
              }`}
            >
              <div className="flex items-start gap-3">
                {submitStatus.type === "success" ? (
                  <Heart className="w-6 h-6 text-green-600 flex-shrink-0" />
                ) : (
                  <X className="w-6 h-6 text-red-600 flex-shrink-0" />
                )}
                <p className={submitStatus.type === "success" ? "text-green-800" : "text-red-800"}>
                  {submitStatus.message}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Memorial Form Modal */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={(e) => e.target === e.currentTarget && setShowForm(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="bg-white rounded-3xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                data-testid="memorial-form-modal"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-serif text-2xl font-bold text-[#1e3a8a]">Share a Memory</h3>
                  <button
                    onClick={() => setShowForm(false)}
                    className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                    data-testid="close-memorial-form"
                  >
                    <X className="w-6 h-6 text-slate-500" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Child's Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      data-testid="memorial-child-name"
                      value={formData.child_name}
                      onChange={(e) => setFormData({ ...formData, child_name: e.target.value })}
                      required
                      placeholder="Enter their name"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Birth Date</label>
                      <input
                        type="text"
                        data-testid="memorial-birth-date"
                        value={formData.birth_date}
                        onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                        placeholder="e.g., March 2020"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Angel Date</label>
                      <input
                        type="text"
                        data-testid="memorial-angel-date"
                        value={formData.angel_date}
                        onChange={(e) => setFormData({ ...formData, angel_date: e.target.value })}
                        placeholder="e.g., May 2020"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Photo URL <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <div className="relative">
                      <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="url"
                        data-testid="memorial-photo-url"
                        value={formData.photo_url}
                        onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                        placeholder="https://..."
                        className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Paste a link to a photo hosted online</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Share a Memory <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      data-testid="memorial-memory"
                      value={formData.memory}
                      onChange={(e) => setFormData({ ...formData, memory: e.target.value })}
                      required
                      rows={4}
                      placeholder="Share a cherished memory, what made them special, or a message from your heart..."
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Your Name</label>
                      <input
                        type="text"
                        data-testid="memorial-submitted-by"
                        value={formData.submitted_by}
                        onChange={(e) => setFormData({ ...formData, submitted_by: e.target.value })}
                        placeholder="Your name"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Relationship</label>
                      <select
                        data-testid="memorial-relationship"
                        value={formData.relationship}
                        onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all bg-white"
                      >
                        <option value="">Select...</option>
                        <option value="Mother">Mother</option>
                        <option value="Father">Father</option>
                        <option value="Parent">Parent</option>
                        <option value="Sibling">Sibling</option>
                        <option value="Grandparent">Grandparent</option>
                        <option value="Family">Family</option>
                        <option value="Friend">Friend</option>
                      </select>
                    </div>
                  </div>

                  <motion.button
                    type="submit"
                    data-testid="submit-memorial-btn"
                    disabled={isSubmitting}
                    className="w-full bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all disabled:opacity-70 flex items-center justify-center gap-2 mt-6"
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Heart className="w-5 h-5" />
                        Share This Memory
                      </>
                    )}
                  </motion.button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Memorial Cards Grid */}
        {memorials.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {memorials.map((memorial, index) => (
              <motion.div
                key={memorial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-3xl shadow-lg overflow-hidden border border-orange-100/50 hover:shadow-xl transition-all group"
                data-testid={`memorial-card-${index}`}
              >
                {/* Photo */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={memorial.photo_url || defaultImage}
                    alt={`In memory of ${memorial.child_name}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = defaultImage; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/80 via-transparent to-transparent" />
                  
                  {/* Name overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="font-serif text-2xl font-bold mb-1">{memorial.child_name}</h3>
                    {(memorial.birth_date || memorial.angel_date) && (
                      <p className="text-blue-100 text-sm flex items-center gap-2">
                        <Star className="w-4 h-4" />
                        {memorial.birth_date && memorial.angel_date 
                          ? `${memorial.birth_date} - ${memorial.angel_date}`
                          : memorial.birth_date || memorial.angel_date}
                      </p>
                    )}
                  </div>

                  {/* Butterfly decoration */}
                  <div className="absolute top-4 right-4">
                    <Butterfly className="w-8 h-8 opacity-50" color="#ffffff" />
                  </div>
                </div>

                {/* Memory Content */}
                <div className="p-6">
                  <p className="text-slate-600 leading-relaxed italic mb-4">
                    "{memorial.memory}"
                  </p>
                  
                  {memorial.submitted_by && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 border-t border-orange-100/50 pt-4">
                      <Heart className="w-4 h-4 text-[#ea580c]" />
                      <span>
                        Shared by {memorial.submitted_by}
                        {memorial.relationship && ` (${memorial.relationship})`}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <Butterfly className="w-24 h-24 mx-auto mb-6 opacity-30" color="#1e3a8a" />
            <h3 className="font-serif text-2xl font-bold text-[#1e3a8a] mb-4">
              Be the First to Share
            </h3>
            <p className="text-slate-600 max-w-md mx-auto mb-8">
              Help us create a beautiful space of remembrance. Share a memory of your angel child 
              and let their light shine on our Memorial Wall.
            </p>
            <motion.button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold px-8 py-4 rounded-full shadow-lg transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus className="w-5 h-5" />
              Add the First Memorial
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

// Events Section
const EventsSection = () => {
  const events = [
    {
      title: "Bereaved Mother's Day Brunch",
      subtitle: "An Afternoon of Honoring, Healing, and Support",
      date: "Sunday, May 3rd, 2026",
      time: "1:00 PM",
      location: "BJ's Event Center",
      address: "1439 Woods Run Ave, PGH, PA 15212",
      cost: "Free and Catered",
      speaker: "Dr. Eleanor Hayes, Grief Specialist & Author",
      description: "To honor angel mothers with a comforted aesthetic.",
      registrationUrl: "https://www.facebook.com/share/1BwNF2JPRr/",
      image: "https://images.unsplash.com/photo-1740782313622-d9a68106498e?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
      featured: true
    },
    {
      title: "Annual Memorial Picnic",
      subtitle: "A Day of Remembrance and Community",
      date: "Saturday, July 4th, 2026",
      time: "12:00 PM",
      location: "TBA",
      address: "Pittsburgh, PA",
      cost: "Free",
      description: "Join us for our annual gathering to honor and remember our beloved children.",
      registrationUrl: "https://www.facebook.com/share/1BwNF2JPRr/",
      image: PHOTOS.community_group,
      featured: false
    }
  ];

  return (
    <section id="events" data-testid="events-section" className="py-24 md:py-32 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #eff6ff 0%, #fff7ed 100%)" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block font-sans text-sm font-semibold text-[#ea580c] tracking-widest uppercase mb-4">
            Upcoming Events
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1e3a8a] mb-6">
            Join Us
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Connect with our community at these upcoming gatherings of hope and healing
          </p>
        </motion.div>

        <div className="space-y-8">
          {events.map((event, index) => (
            <motion.div
              key={event.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-[2rem] shadow-xl overflow-hidden"
              data-testid={`event-card-${index}`}
            >
              <div className={`grid ${event.featured ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
                {/* Event Image */}
                <div className={`relative ${event.featured ? 'h-[300px] md:h-auto' : 'h-[200px] md:h-auto'}`}>
                  <img
                    src={event.image}
                    alt={event.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a8a]/70 to-transparent md:bg-gradient-to-t md:from-[#1e3a8a]/70 md:to-transparent" />
                  <div className="absolute bottom-6 left-6 text-white">
                    <div className={`${event.featured ? 'bg-[#ea580c]' : 'bg-[#1e3a8a]'} text-white px-4 py-2 rounded-full inline-block`}>
                      <Calendar className="w-4 h-4 inline mr-2" />
                      {event.featured ? 'Featured Event' : 'Upcoming'}
                    </div>
                  </div>
                </div>

                {/* Event Details */}
                <div className={`p-8 ${event.featured ? 'md:p-12' : 'md:col-span-2 md:p-8'}`}>
                  <h3 className={`font-serif ${event.featured ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'} font-bold text-[#1e3a8a] mb-2`}>
                    {event.title}
                  </h3>
                  <p className="text-[#ea580c] font-medium mb-6">{event.subtitle}</p>
                  
                  <div className={`grid ${event.featured ? 'grid-cols-1' : 'md:grid-cols-3'} gap-4 mb-6`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#1e3a8a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-[#1e3a8a]" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{event.date}</p>
                        <p className="text-slate-500 text-sm">{event.time}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#1e3a8a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-[#1e3a8a]" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{event.location}</p>
                        <p className="text-slate-500 text-sm">{event.address}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#ea580c]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Heart className="w-5 h-5 text-[#ea580c]" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">Cost: {event.cost}</p>
                        {event.speaker && <p className="text-slate-500 text-sm">Guest: {event.speaker}</p>}
                      </div>
                    </div>
                  </div>

                  <p className="text-slate-600 mb-6 italic">"{event.description}"</p>

                  <motion.a
                    href={event.registrationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`event-register-btn-${index}`}
                    className="inline-flex items-center gap-2 bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold px-6 py-3 rounded-full transition-all"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Facebook className="w-4 h-4" />
                    Register on Facebook
                    <ExternalLink className="w-4 h-4" />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Burial Assistance Challenge Section
const BurialChallengeSection = () => {
  const [isLoading, setIsLoading] = useState(false);
  const challengeGoal = 100000;
  const [currentAmount] = useState(0); // This would come from backend in production

  const handleChallengeDonate = async (amount) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/donations/create-checkout`, {
        amount_key: "custom",
        custom_amount: amount,
        origin_url: window.location.origin,
        donor_name: null,
        donor_email: null,
        message: "Burial Assistance Challenge"
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Donation error:", error);
      alert("Failed to initiate donation. Please try again.");
    }
    setIsLoading(false);
  };

  const progressPercent = Math.min((currentAmount / challengeGoal) * 100, 100);

  return (
    <section id="challenge" data-testid="challenge-section" className="py-20 md:py-28 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] relative overflow-hidden">
      {/* Decorative elements */}
      <Butterfly className="absolute top-10 right-[10%] w-32 h-32 opacity-10" color="#ffffff" />
      <Butterfly className="absolute bottom-10 left-[5%] w-24 h-24 opacity-10" color="#ea580c" />
      
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          {/* Challenge Badge */}
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-[#ea580c] text-white px-6 py-2 rounded-full mb-6"
          >
            <Target className="w-5 h-5" />
            <span className="font-bold tracking-wide">FUNDRAISING CHALLENGE</span>
          </motion.div>

          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Burial Assistance Fund
          </h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8 leading-relaxed">
            Help us provide burial assistance to families who have lost a child. 
            No family should face financial burden while grieving.
          </p>

          {/* Progress Bar */}
          <div className="max-w-xl mx-auto mb-8">
            <div className="flex justify-between text-sm text-blue-200 mb-2">
              <span>Raised: ${currentAmount.toLocaleString()}</span>
              <span>Goal: ${challengeGoal.toLocaleString()}</span>
            </div>
            <div className="h-4 bg-white/20 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${progressPercent}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.5 }}
                className="h-full bg-gradient-to-r from-[#ea580c] to-[#f97316] rounded-full"
              />
            </div>
            <p className="text-blue-200 text-sm mt-2">{progressPercent.toFixed(0)}% of goal reached</p>
          </div>

          {/* Challenge Donation Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {[25, 50, 100, 250, 500].map((amount) => (
              <motion.button
                key={amount}
                data-testid={`challenge-amount-${amount}`}
                onClick={() => handleChallengeDonate(amount)}
                disabled={isLoading}
                className="bg-white hover:bg-orange-50 text-[#1e3a8a] font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all disabled:opacity-70"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                ${amount}
              </motion.button>
            ))}
          </div>

          <motion.button
            data-testid="challenge-custom-btn"
            onClick={() => document.getElementById("donate")?.scrollIntoView({ behavior: "smooth" })}
            className="inline-flex items-center gap-2 border-2 border-white/50 hover:border-white text-white font-semibold px-8 py-4 rounded-full transition-all"
            whileHover={{ scale: 1.05 }}
          >
            <Gift className="w-5 h-5" />
            Give a Custom Amount
          </motion.button>

          {/* Info Cards */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: Heart, title: "100% Goes to Families", desc: "Every dollar helps cover burial costs for grieving families" },
              { icon: Users, title: "Community Support", desc: "Join others in making a meaningful difference" },
              { icon: HandHeart, title: "Immediate Impact", desc: "Funds are distributed directly to families in need" }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-left"
              >
                <item.icon className="w-8 h-8 text-[#ea580c] mb-3" />
                <h4 className="font-bold text-white mb-2">{item.title}</h4>
                <p className="text-blue-200 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Donation Section
const DonationSection = () => {
  const [selectedAmount, setSelectedAmount] = useState("50");
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const donationAmounts = ["25", "50", "100", "250"];

  // Check for payment return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const sessionId = params.get("session_id");

    if (payment === "success" && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (payment === "cancelled") {
      setPaymentStatus({ type: "cancelled", message: "Payment was cancelled. You can try again anytime." });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus({ type: "pending", message: "Payment is being processed. You will receive confirmation shortly." });
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    try {
      const response = await axios.get(`${API}/donations/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setPaymentStatus({ type: "success", message: "Thank you for your generous donation! Your support means the world to us.", amount: data.amount });
        window.history.replaceState({}, "", window.location.pathname);
        return;
      } else if (data.status === "expired") {
        setPaymentStatus({ type: "error", message: "Payment session expired. Please try again." });
        window.history.replaceState({}, "", window.location.pathname);
        return;
      }

      setPaymentStatus({ type: "processing", message: "Processing your payment..." });
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setPaymentStatus({ type: "error", message: "Error checking payment status. Please contact us if you were charged." });
    }
  };

  const handleDonate = async () => {
    setIsLoading(true);
    try {
      const amountKey = selectedAmount === "custom" ? "custom" : selectedAmount;
      const customAmountValue = selectedAmount === "custom" ? parseFloat(customAmount) : null;

      if (selectedAmount === "custom" && (!customAmountValue || customAmountValue < 1)) {
        alert("Please enter a valid amount (minimum $1)");
        setIsLoading(false);
        return;
      }

      const response = await axios.post(`${API}/donations/create-checkout`, {
        amount_key: amountKey,
        custom_amount: customAmountValue,
        origin_url: window.location.origin,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        message: message || null
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Donation error:", error);
      alert("Failed to initiate donation. Please try again.");
    }
    setIsLoading(false);
  };

  return (
    <section id="donate" data-testid="donate-section" className="py-24 md:py-32 bg-[#fffaf0] relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block font-sans text-sm font-semibold text-[#ea580c] tracking-widest uppercase mb-4">
              Support Our Mission
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1e3a8a] mb-6 blue-accent-line">
              Make a Donation
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Your generosity helps us provide free support programs, counseling, and community events 
              for bereaved families. Every donation directly impacts a family's healing journey and 
              ensures no parent or sibling journeys alone through loss.
            </p>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100/50">
              <h4 className="font-serif font-bold text-[#1e3a8a] mb-4">Your donation supports:</h4>
              <ul className="space-y-3">
                {[
                  "Free support groups for bereaved parents and siblings",
                  "Counseling and specialized services",
                  "Community events and memorial gatherings",
                  "Accessible resources for families in need",
                  "Educational programs on grief and healing"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-600">
                    <Heart className="w-4 h-4 text-[#ea580c] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Donation Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {/* Payment Status Message */}
            <AnimatePresence>
              {paymentStatus && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  data-testid="payment-status"
                  className={`mb-6 p-6 rounded-2xl ${
                    paymentStatus.type === "success" ? "bg-green-50 border border-green-200" :
                    paymentStatus.type === "error" || paymentStatus.type === "cancelled" ? "bg-red-50 border border-red-200" :
                    "bg-blue-50 border border-blue-200"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {paymentStatus.type === "success" && <Check className="w-6 h-6 text-green-600 flex-shrink-0" />}
                    {paymentStatus.type === "processing" && <Loader2 className="w-6 h-6 text-blue-600 animate-spin flex-shrink-0" />}
                    <div>
                      <p className={`font-semibold ${
                        paymentStatus.type === "success" ? "text-green-800" :
                        paymentStatus.type === "error" || paymentStatus.type === "cancelled" ? "text-red-800" :
                        "text-blue-800"
                      }`}>
                        {paymentStatus.type === "success" ? "Thank You!" : 
                         paymentStatus.type === "processing" ? "Processing..." :
                         paymentStatus.type === "cancelled" ? "Cancelled" : "Notice"}
                      </p>
                      <p className="text-slate-600 mt-1">{paymentStatus.message}</p>
                      {paymentStatus.amount && (
                        <p className="text-green-700 font-bold mt-2">Amount: ${paymentStatus.amount}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="bg-white rounded-3xl p-8 shadow-xl border border-orange-100/50" data-testid="donation-form">
              <h3 className="font-serif text-xl font-bold text-[#1e3a8a] mb-6">Select Amount</h3>
              
              {/* Amount Buttons */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {donationAmounts.map((amount) => (
                  <motion.button
                    key={amount}
                    data-testid={`amount-${amount}`}
                    onClick={() => setSelectedAmount(amount)}
                    className={`py-4 px-6 rounded-xl font-bold text-lg transition-all ${
                      selectedAmount === amount
                        ? "bg-[#1e3a8a] text-white shadow-lg"
                        : "bg-[#f1f5f9] text-slate-700 hover:bg-[#e2e8f0]"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    ${amount}
                  </motion.button>
                ))}
              </div>
              
              {/* Custom Amount */}
              <motion.button
                data-testid="amount-custom"
                onClick={() => setSelectedAmount("custom")}
                className={`w-full py-4 px-6 rounded-xl font-bold transition-all mb-4 ${
                  selectedAmount === "custom"
                    ? "bg-[#1e3a8a] text-white shadow-lg"
                    : "bg-[#f1f5f9] text-slate-700 hover:bg-[#e2e8f0]"
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Custom Amount
              </motion.button>
              
              {selectedAmount === "custom" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="mb-4"
                >
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                    <input
                      type="number"
                      min="1"
                      data-testid="custom-amount-input"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="Enter amount"
                      className="w-full pl-8 pr-4 py-4 rounded-xl border border-slate-200 focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                    />
                  </div>
                </motion.div>
              )}

              {/* Optional Fields */}
              <div className="space-y-4 mb-6">
                <input
                  type="text"
                  data-testid="donor-name-input"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  placeholder="Your Name (optional)"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                />
                <input
                  type="email"
                  data-testid="donor-email-input"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  placeholder="Your Email (optional)"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                />
                <textarea
                  data-testid="donor-message-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Leave a message of hope (optional)"
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all resize-none"
                />
              </div>

              {/* Donate Button */}
              <motion.button
                data-testid="donate-submit-btn"
                onClick={handleDonate}
                disabled={isLoading}
                className="w-full bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                whileHover={{ scale: isLoading ? 1 : 1.02 }}
                whileTap={{ scale: isLoading ? 1 : 0.98 }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Heart className="w-5 h-5" />
                    Donate ${selectedAmount === "custom" ? customAmount || "0" : selectedAmount}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>

              <p className="text-center text-sm text-slate-500 mt-4">
                Secure payment powered by Stripe
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus({ type: "success", message: "Thank you! We'll be in touch soon." });
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      setSubmitStatus({ type: "error", message: "Failed to send message. Please try again." });
    }
    setIsSubmitting(false);
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-white relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block font-sans text-sm font-semibold text-[#ea580c] tracking-widest uppercase mb-4">
              Get In Touch
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1e3a8a] mb-6 blue-accent-line">
              Contact Us
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              We'd love to hear from you. Whether you have questions about our programs, 
              want to volunteer, or simply need someone to talk to, reach out. You don't 
              have to walk this journey alone.
            </p>

            <div className="space-y-6">
              <motion.a
                href="mailto:aj.chacesway@gmail.com"
                data-testid="contact-email-link"
                className="flex items-center gap-4 group"
                whileHover={{ x: 5 }}
              >
                <div className="w-14 h-14 bg-[#ea580c]/10 rounded-2xl flex items-center justify-center group-hover:bg-[#ea580c] transition-colors">
                  <Mail className="w-6 h-6 text-[#ea580c] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Email Us</p>
                  <p className="text-[#ea580c]">aj.chacesway@gmail.com</p>
                </div>
              </motion.a>

              <motion.a
                href="https://www.facebook.com/share/1BwNF2JPRr/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook-link"
                className="flex items-center gap-4 group"
                whileHover={{ x: 5 }}
              >
                <div className="w-14 h-14 bg-[#1e3a8a]/10 rounded-2xl flex items-center justify-center group-hover:bg-[#1e3a8a] transition-colors">
                  <Facebook className="w-6 h-6 text-[#1e3a8a] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Facebook</p>
                  <p className="text-[#1e3a8a]">Chaces Way</p>
                </div>
              </motion.a>

              <motion.a
                href="https://www.instagram.com/chaces.way"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="flex items-center gap-4 group"
                whileHover={{ x: 5 }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-purple-500 group-hover:to-pink-500 transition-colors">
                  <Instagram className="w-6 h-6 text-pink-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Instagram</p>
                  <p className="text-pink-600">@chaces.way</p>
                </div>
              </motion.a>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#1e3a8a]/10 rounded-2xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#1e3a8a]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Location</p>
                  <p className="text-slate-600">Pittsburgh, Pennsylvania</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="bg-[#fefce8] rounded-3xl p-8" data-testid="contact-form">
              <h3 className="font-serif text-xl font-bold text-[#1e3a8a] mb-6">Send a Message</h3>
              
              {submitStatus && (
                <div className={`mb-4 p-4 rounded-xl ${submitStatus.type === "success" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`} data-testid="contact-status">
                  {submitStatus.message}
                </div>
              )}

              <div className="space-y-4">
                <input
                  type="text"
                  data-testid="contact-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your Name"
                  required
                  className="w-full px-4 py-4 rounded-xl border border-slate-200 bg-white focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                />
                <input
                  type="email"
                  data-testid="contact-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Your Email"
                  required
                  className="w-full px-4 py-4 rounded-xl border border-slate-200 bg-white focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                />
                <textarea
                  data-testid="contact-message-input"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Your Message"
                  required
                  rows={5}
                  className="w-full px-4 py-4 rounded-xl border border-slate-200 bg-white focus:border-[#1e3a8a] focus:ring-2 focus:ring-blue-100 outline-none transition-all resize-none"
                />
                <motion.button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                  className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all disabled:opacity-70"
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    "Send Message"
                  )}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#1e3a8a] text-white py-16 relative overflow-hidden">
      <Butterfly className="absolute -right-20 -bottom-20 w-64 h-64 opacity-5" color="#ffffff" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div className="grid md:grid-cols-3 gap-12">
          {/* Logo & Mission */}
          <div>
            <div className="mb-4">
              <span className="font-serif font-bold text-2xl text-[#ea580c]">Chaces Way</span>
              <p className="text-blue-200 text-sm mt-1">Bereaved Support Group</p>
            </div>
            <p className="text-blue-100 leading-relaxed">
              Let's meet at the corner of hope and healing. Providing support, comfort, 
              and empowerment to bereaved families.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { label: "Home", id: "home" },
                { label: "Our Mission", id: "mission" },
                { label: "Our Story", id: "founder" },
                { label: "Memorial Wall", id: "memorial" },
                { label: "Events", id: "events" },
                { label: "Donate", id: "donate" },
                { label: "Contact", id: "contact" }
              ].map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="text-blue-200 hover:text-[#ea580c] transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(item.id)?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif font-bold text-lg mb-4">Contact</h4>
            <a
              href="mailto:aj.chacesway@gmail.com"
              className="flex items-center gap-2 text-blue-200 hover:text-[#ea580c] transition-colors mb-3"
            >
              <Mail className="w-4 h-4" />
              aj.chacesway@gmail.com
            </a>
            <a
              href="https://www.facebook.com/share/1BwNF2JPRr/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-200 hover:text-[#ea580c] transition-colors mb-3"
            >
              <Facebook className="w-4 h-4" />
              Facebook: Chaces Way
            </a>
            <a
              href="https://www.instagram.com/chaces.way"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-200 hover:text-[#ea580c] transition-colors mb-3"
            >
              <Instagram className="w-4 h-4" />
              Instagram: @chaces.way
            </a>
            <p className="flex items-center gap-2 text-blue-200 mt-3">
              <MapPin className="w-4 h-4" />
              Pittsburgh, Pennsylvania
            </p>
          </div>
        </div>

        <div className="border-t border-blue-700 mt-12 pt-8 text-center text-blue-200 text-sm">
          <p>&copy; {new Date().getFullYear()} Chace's Way Bereaved Support Group. All rights reserved.</p>
          <p className="mt-2">Let's Meet At The Corner Of Hope and Healing</p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["home", "mission", "founder", "memorial", "events", "donate", "contact"];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App" data-testid="app-container">
      <Navigation activeSection={activeSection} />
      <HeroSection />
      <MissionSection />
      <FounderSection />
      <MemorialWallSection />
      <EventsSection />
      <BurialChallengeSection />
      <DonationSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
